import React, { useState, useCallback, useEffect } from 'react';
import { Modal, TextInput } from '@mantine/core';
import { Button } from '../../../../../components/Button';
import type { OrderView } from '../../../../../services/orders/orders.service';
import classes from './guest-name-edit-modal.module.scss';
import { noSelectionTextInputProps } from '../receipt-input.utils';

export type GuestNameEditModalProps = {
    order: OrderView;
    opened: boolean;
    onClose: () => void;
    onSave: (orderId: number, guestName: string | null) => Promise<boolean>;
    isSaving: boolean;
};

const GuestNameEditModalComponent: React.FC<GuestNameEditModalProps> = ({
    order,
    opened,
    onClose,
    onSave,
    isSaving,
}) => {
    const [draft, setDraft] = useState(order.guestName ?? '');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (opened) {
            setDraft(order.guestName ?? '');
        }
    }, [opened, order.guestName]);

    const isBusy = isSaving || isSubmitting;

    const handleSave = useCallback(async () => {
        if (isBusy) {
            return;
        }
        const trimmed = draft.trim();
        setIsSubmitting(true);
        try {
            const didSave = await onSave(order.id, trimmed.length > 0 ? trimmed : null);
            if (didSave) {
                onClose();
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [draft, isBusy, onClose, onSave, order.id]);

    const handleClose = useCallback(() => {
        setDraft(order.guestName ?? '');
        onClose();
    }, [order.guestName, onClose]);

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="הזן שם לקוח"
            size="sm"
            yOffset={24}
            radius="md"
            dir="rtl"
            classNames={{ root: classes.modalRoot }}
            withCloseButton={false}
        >
            <div className={classes.modalBodyContainer}>
                <TextInput
                    placeholder="הזן שם לקוח"
                    value={draft}
                    onChange={(e) => setDraft(e.currentTarget.value)}
                    className={classes.guestInput}
                    aria-label="שם לקוח"
                    maxLength={35}
                    data-testid="guest-name-input"
                    data-autofocus
                    {...noSelectionTextInputProps}
                />
                <div className={classes.buttonRow}>
                    <Button variant="cancel" fullWidth onClick={handleClose} disabled={isBusy} dataTestId="guest-name-cancel">
                        ביטול
                    </Button>
                    <Button
                        variant="confirm"
                        fullWidth
                        onClick={handleSave}
                        disabled={isBusy}
                        dataTestId="guest-name-save"
                    >
                        {isBusy ? 'טוען...' : 'אישור'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export const GuestNameEditModal = React.memo(GuestNameEditModalComponent);
